import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import AuthModal from './components/auth/AuthModal';
import './App.css';

// Layout
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';

// Sections
import Hero from './components/sections/Hero';
import ClientGalleries from './components/sections/ClientGalleries';
import VideoDelivery from './components/sections/VideoDelivery';
import VendorNetwork from './components/sections/VendorNetwork';
import AISearch from './components/sections/AISearch';
import SmartStore from './components/sections/SmartStore';
import PrintLabs from './components/sections/PrintLabs';
import MarketingAutomation from './components/sections/MarketingAutomation';
import AlbumBuilder from './components/sections/AlbumBuilder';
import Slideshows from './components/sections/Slideshows';
import Blogs from './components/sections/Blogs';
import ArtGalleries from './components/sections/ArtGalleries';
import CRMIntegrations from './components/sections/CRMIntegrations';
import Migration from './components/sections/Migration';
import CTA from './components/sections/CTA';

// Pages
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminGalleryDetail from './pages/AdminGalleryDetail';
import AdminSettingsPage from './pages/AdminSettingsPage';
import ClientGalleryView from './pages/ClientGalleryView';
import ClientVideosView from './pages/ClientVideosView';
import GuestGalleryPage from './pages/GuestGalleryPage';
import InstaweddingPage from './pages/InstaweddingPage';
import PhotoBoothPage from './pages/PhotoBoothPage';

const HomePage = () => {
  const [showAuth, setShowAuth] = useState(false);
  
  return (
    <div className="min-h-screen bg-sky-100 flex flex-col items-center justify-center relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-sky-50 to-sky-200 opacity-50" />
      
      {/* Main content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        {/* Title */}
        <h1 className="font-wedding text-5xl md:text-7xl text-gray-800 mb-6">
          Le foto e i video del tuo giorno più bello
        </h1>
        
        {/* Photo */}
        <div className="my-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_d7381ff1-b086-4726-86cd-0149a62640b6/artifacts/ra8u9tgb_sposi.png"
            alt="Sposi"
            className="max-w-full max-h-[50vh] mx-auto rounded-2xl shadow-2xl"
          />
        </div>
        
        {/* Access button */}
        <button
          onClick={() => setShowAuth(true)}
          className="px-8 py-4 bg-white text-gray-800 rounded-full text-lg font-medium shadow-lg hover:shadow-xl transition-all hover:scale-105"
        >
          Accesso
        </button>
      </div>
      
      {/* Auth Modal */}
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate page based on role
    if (user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/gallery" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              
              {/* Admin routes */}
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/gallery/:id" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminGalleryDetail />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/settings" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminSettingsPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Gallery routes (sposi & amici) */}
              <Route 
                path="/gallery" 
                element={
                  <ProtectedRoute allowedRoles={['sposi', 'amici']}>
                    <ClientGalleryView />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/videos" 
                element={
                  <ProtectedRoute allowedRoles={['sposi', 'amici']}>
                    <ClientVideosView />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/instawedding" 
                element={
                  <ProtectedRoute allowedRoles={['sposi', 'amici']}>
                    <InstaweddingPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/photobooth" 
                element={
                  <ProtectedRoute allowedRoles={['sposi', 'amici']}>
                    <PhotoBoothPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Public guest upload page */}
              <Route path="/event/:galleryId" element={<GuestGalleryPage />} />
              
              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </div>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
